import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Form, Table, Input, Select, Checkbox, Button, Space, Tooltip, Dropdown, Menu } from 'antd';
import { PlusOutlined, DeleteOutlined, CopyOutlined, SettingOutlined, CloseOutlined, ReloadOutlined } from '@ant-design/icons';
import { useAntdResizableHeader } from 'use-antd-resizable-header';
import 'use-antd-resizable-header/dist/style.css';
import type { DataGridConfig, ValueChangeEvent, GridColumn, GridRow, DropdownOption } from '../types';
import { getDataGridOptions } from '../types';
import { telemetryService } from '../../../api';
import { dropdownCache } from '../utils/dropdownCache';
import { useStore, useSelectedNodeId } from '../../../store/store';
import { createContextLogger } from '../../../utils/logger';

const logger = createContextLogger('DataGrid');

interface DataGridProps {
  config: DataGridConfig;
  value?: GridRow[];
  onChange: (event: ValueChangeEvent) => void;
  pageId: string;
  error?: string[];
}

export const DataGrid: React.FC<DataGridProps> = ({
  config,
  value = [],
  onChange,
  pageId,
  error
}) => {
  // Use a ref to always have the latest value
  const valueRef = useRef(value);
  valueRef.current = value;
  
  // Debug: Track incoming value prop changes
  React.useEffect(() => {
    logger.debug('GridTest 🔄 DataGrid value prop changed:', {
      componentId: config.id,
      valueLength: value?.length || 0,
      value: value?.map(row => ({ ...row })) || []
    });
  }, [value, config.id]);
  
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [dynamicColumnOptions, setDynamicColumnOptions] = useState<Record<string, DropdownOption[]>>({});
  const [loadingColumns, setLoadingColumns] = useState<Record<string, boolean>>({});
  
  // Get stable selected node ID
  const { nodeId } = useSelectedNodeId();
  
  // Get pipeline and transform context for cache scoping
  const { activePipelinePath, selectedNode, isWorkflow } = useStore((state) => {
    const activePipeline = state.pipelines.find((p) => p.id === state.activePipelineId);
    const selectedNode = activePipeline?.nodes.find((n) => n.id === nodeId);
    return {
      activePipelinePath: activePipeline?.id, // Pipeline ID is actually the file path
      selectedNode,
      isWorkflow: activePipeline?.type === 'workflow'
    };
  });
  
  // Extract data grid specific options from additionalOptions
  const gridOptions = getDataGridOptions(config);
  

  

  
  // Clear options when pipeline context changes (but not on every node data update)
  const stablePipelinePath = useRef<string | null>(null);
  const stableTransformType = useRef<string | null>(null);
  
  useEffect(() => {
    const currentPipelinePath = activePipelinePath || null;
    const currentTransformType = selectedNode?.data?.type || null;
    
    // Only clear if pipeline or transform type actually changed
    if (stablePipelinePath.current !== currentPipelinePath || 
        stableTransformType.current !== currentTransformType) {
      
      logger.debug('Clearing dynamic column options due to context change:', {
        configId: config.id,
        oldPipeline: stablePipelinePath.current,
        newPipeline: currentPipelinePath,
        oldTransformType: stableTransformType.current,
        newTransformType: currentTransformType
      });
      
      setDynamicColumnOptions({});
      setLoadingColumns({});
      
      // Update refs
      stablePipelinePath.current = currentPipelinePath;
      stableTransformType.current = currentTransformType;
    }
  }, [activePipelinePath, selectedNode?.data?.type]);

  // Debug: Log when dynamicColumnOptions state changes
  useEffect(() => {
    if (Object.keys(dynamicColumnOptions).length > 0) {
      logger.debug('✅ dynamicColumnOptions updated:', {
        configId: config.id,
        optionsCounts: Object.entries(dynamicColumnOptions).map(([key, options]) => ({
          key,
          count: options?.length || 0
        }))
      });
    }
  }, [dynamicColumnOptions]);
  
  // Fetch dynamic options for columns that have dynamicLoad enabled
  useEffect(() => {
    const fetchDynamicOptions = async () => {
      if (!gridOptions.columns || !activePipelinePath || !selectedNode?.data?.type) return;
      
      const dynamicColumns = gridOptions.columns.filter(
        (col: any) => col.type === 'dropdown' && col.dynamicLoad && col.transformType && col.componentId
      );
      
      if (dynamicColumns.length === 0) return;
      
      // Set loading state for all dynamic columns
      const newLoadingState: Record<string, boolean> = {};
      dynamicColumns.forEach((col: any) => {
        newLoadingState[col.id] = true;
      });
      setLoadingColumns(newLoadingState);
      
      try {
        // Fetch options using the cache manager
        const transformName = selectedNode?.id || 'unknown';
        const optionsPromises = dynamicColumns.map(async (col: any) => {
          try {
            const options = await dropdownCache.getOptions({
              pipelinePath: activePipelinePath,
              transformType: col.transformType,
              componentId: col.componentId,
              transformName: transformName,
              isWorkflow: isWorkflow
            });
            return { columnId: col.id, options };
          } catch (error) {
            logger.error(`Failed to fetch options for column ${col.id}:`, error);
            return { columnId: col.id, options: [] };
          }
        });
        
        const results = await Promise.all(optionsPromises);
        
        const newOptions: Record<string, DropdownOption[]> = {};
        const newLoadingState: Record<string, boolean> = {};
        
        results.forEach(({ columnId, options }) => {
          newOptions[columnId] = options;
          newLoadingState[columnId] = false;
        });
        
        setDynamicColumnOptions(newOptions);
        setLoadingColumns(newLoadingState);
        
        logger.debug('✅ Dynamic column options loaded:', {
          configId: config.id,
          optionsCounts: Object.entries(newOptions).map(([key, options]) => ({
            key,
            count: options?.length || 0
          }))
        });
      } catch (error) {
        logger.error('Failed to fetch dynamic column options:', error);
        const newLoadingState: Record<string, boolean> = {};
        dynamicColumns.forEach((col: any) => {
          newLoadingState[col.id] = false;
        });
        setLoadingColumns(newLoadingState);
      }
    };
    
    fetchDynamicOptions();
  }, [gridOptions.columns, activePipelinePath, selectedNode?.data?.type]);
  
  // Helper function to compare values (handles different data types)
  const isEqual = useCallback((value1: any, value2: any) => {
    // Handle null/undefined cases
    if (value1 === null || value1 === undefined) {
      return value2 === null || value2 === undefined;
    }
    if (value2 === null || value2 === undefined) {
      return value1 === null || value1 === undefined;
    }
    
    // For arrays and objects, do deep comparison
    if (Array.isArray(value1) && Array.isArray(value2)) {
      return JSON.stringify(value1) === JSON.stringify(value2);
    }
    if (typeof value1 === 'object' && typeof value2 === 'object') {
      return JSON.stringify(value1) === JSON.stringify(value2);
    }
    
    // For primitives, direct comparison
    return value1 === value2;
  }, []);

  // Helper function to check if a column has data
  const columnHasData = useCallback((columnId: string, data: GridRow[]) => {
    if (!data || data.length === 0) return false;
    
    // Find the column configuration to check for defaultValue
    const column = gridOptions.columns?.find(col => col.id === columnId);
    if (!column) {
      return false;
    }
    
    const hasDefaultValue = column && column.defaultValue !== undefined;
    
    return data.some(row => {
      const cellValue = row[columnId];
      
      if (hasDefaultValue) {
        // If column has a defaultValue defined, check if any cell differs from it
        // Treat undefined/null values as the default value for comparison
        const effectiveValue = (cellValue === undefined || cellValue === null) ? column.defaultValue : cellValue;
        const differs = !isEqual(effectiveValue, column.defaultValue);
        
        // Debug logging for storageType column
        if (columnId === 'storageType') {
          logger.debug('StorageType column check:', {
            cellValue,
            effectiveValue,
            defaultValue: column.defaultValue,
            differs,
            rowData: row
          });
        }
        
        return differs;
      } else {
        // If no defaultValue, use standard empty check
        return cellValue !== null && cellValue !== undefined && cellValue !== '';
      }
    });
  }, [gridOptions.columns, isEqual]);

  // Initialize visible columns based on requirements and data content
  const [visibleColumnIds, setVisibleColumnIds] = useState<Set<string>>(() => {
    const initialVisible = new Set<string>();
    
    // Safety check: ensure gridOptions.columns exists and is an array
    if (gridOptions.columns && Array.isArray(gridOptions.columns)) {
      gridOptions.columns.forEach(column => {
        // Always show required columns
        if (column.required) {
          initialVisible.add(column.id);
        }
        // Always show non-optional columns (these are typically essential like fieldname)
        else if (!column.optional) {
          initialVisible.add(column.id);
        }
        // Handle optional columns
        else if (column.optional) {
          // Always show if explicitly marked as defaultVisible
          if (column.defaultVisible === true) {
            initialVisible.add(column.id);
          }
          // For columns with defaultValue, only show if they have meaningful data
          else if (column.defaultValue !== undefined) {
            // If there's data, check if any differs from default
            if (columnHasData(column.id, value)) {
              initialVisible.add(column.id);
            }
            // If no data yet, assume it will be default values - hide the column
          }
          // For columns without defaultValue, show if they have any data
          else if (columnHasData(column.id, value)) {
            initialVisible.add(column.id);
          }
        }
      });
    } else {
      const error = new Error('DataGrid configuration error: columns is not an array');
      logger.error('DataGrid configuration error:', {
        componentId: config.id,
        configColumns: gridOptions.columns,
        configColumnsType: typeof gridOptions.columns
      });
      telemetryService.trackError(error, 'datagrid-config', {
        componentId: config.id,
        configColumnsType: typeof gridOptions.columns,
        context: 'column-initialization-failure'
      });
    }
    
    return initialVisible;
  });

  // Update visible columns when data changes (to show columns that gain data)
  useEffect(() => {
    if (gridOptions.columns && Array.isArray(gridOptions.columns)) {
      setVisibleColumnIds(prev => {
        const updated = new Set(prev);
        let hasChanges = false;

        gridOptions.columns.forEach(column => {
          const isCurrentlyVisible = prev.has(column.id);
          
          // Determine if column should be visible using same logic as initialization
          let shouldBeVisible = false;
          
          if (column.required) {
            shouldBeVisible = true;
          } else if (!column.optional) {
            shouldBeVisible = true;
          } else if (column.optional) {
            if (column.defaultVisible === true) {
              shouldBeVisible = true;
            } else if (column.defaultValue !== undefined) {
              // For columns with defaultValue, show only if data differs from default
              shouldBeVisible = columnHasData(column.id, value);
            } else {
              // For columns without defaultValue, show if they have any data
              shouldBeVisible = columnHasData(column.id, value);
            }
          }

          if (!isCurrentlyVisible && shouldBeVisible) {
            updated.add(column.id);
            hasChanges = true;
          } else if (isCurrentlyVisible && !shouldBeVisible) {
            // Hide columns that no longer have meaningful data (only for optional columns with defaultValue)
            if (column.optional && column.defaultValue !== undefined) {
              updated.delete(column.id);
              hasChanges = true;
            }
          }
        });

        return hasChanges ? updated : prev;
      });
    }
  }, [value, gridOptions.columns, columnHasData, isEqual]);

  // Get visible columns for rendering
  const visibleColumns = useMemo(() => {
    // Safety check: ensure gridOptions.columns exists and is an array
    if (!gridOptions.columns || !Array.isArray(gridOptions.columns)) {
      return [];
    }
    return gridOptions.columns.filter(column => visibleColumnIds.has(column.id));
  }, [gridOptions.columns, visibleColumnIds]);

  // Get optional columns that can be added
  const availableOptionalColumns = useMemo(() => {
    // Safety check: ensure gridOptions.columns exists and is an array
    if (!gridOptions.columns || !Array.isArray(gridOptions.columns)) {
      return [];
    }
    return gridOptions.columns.filter(column => 
      column.optional && !visibleColumnIds.has(column.id)
    );
  }, [gridOptions.columns, visibleColumnIds]);

  // Helper function to generate default row data based on column configurations
  const generateDefaultRowData = useCallback(() => {
    const defaultData: any = { id: `${Date.now()}` };
    
    // Apply column default values
    if (gridOptions.columns && Array.isArray(gridOptions.columns)) {
      gridOptions.columns.forEach(column => {
        if (column.defaultValue !== undefined) {
          defaultData[column.id] = column.defaultValue;
        } else {
          // Initialize with appropriate default based on column type
          switch (column.type) {
            case 'text':
            case 'dropdown':
              defaultData[column.id] = '';
              break;
            case 'checkbox':
              defaultData[column.id] = false;
              break;
            case 'number':
              defaultData[column.id] = '';
              break;
            default:
              defaultData[column.id] = '';
          }
        }
      });
    }
    
    // Merge with any additional default row data from gridOptions
    return {
      ...defaultData,
      ...gridOptions.defaultRowData || {}
    };
  }, [gridOptions.columns, gridOptions.defaultRowData]);

  const handleCellChange = useCallback((rowIndex: number, columnId: string, newValue: any) => {
    // Use the ref to get the latest value, avoiding stale closure
    const currentValue = valueRef.current;
    
    logger.debug('GridTest 🔧 handleCellChange with current data from ref:', { 
      rowIndex, 
      columnId, 
      newValue, 
      currentValueLength: currentValue.length,
      currentValue: currentValue.map(row => ({ ...row }))
    });
    
    const newData = [...currentValue];
    
    logger.debug('GridTest 📋 After spreading from ref - newData length:', newData.length);
    
    if (!newData[rowIndex]) {
      logger.debug('GridTest 🆕 Creating new row at index', rowIndex);
      newData[rowIndex] = generateDefaultRowData();
    }
    
    newData[rowIndex][columnId] = newValue;
    
    logger.debug('GridTest 📤 Sending updated data:', newData.map(row => ({ ...row })));

    onChange({
      componentId: config.id,
      value: newData,
      pageId
    });
  }, [onChange, config.id, pageId, generateDefaultRowData]);

  const handleAddRow = useCallback(() => {
    const newRow: GridRow = generateDefaultRowData();
    const newData = [...value, newRow];

    onChange({
      componentId: config.id,
      value: newData,
      pageId
    });
  }, [value, onChange, config.id, pageId, generateDefaultRowData]);

  const handleDeleteRows = useCallback(() => {
    const newData = value.filter((_, index) => !selectedRowKeys.includes(index));
    setSelectedRowKeys([]);

    onChange({
      componentId: config.id,
      value: newData,
      pageId
    });
  }, [value, selectedRowKeys, onChange, config.id, pageId]);

  const handleDuplicateRows = useCallback(() => {
    const rowsToDuplicate = value.filter((_, index) => selectedRowKeys.includes(index));
    const duplicatedRows = rowsToDuplicate.map(row => ({
      ...row,
      id: `row_${Date.now()}_${Math.random()}`
    }));
    const newData = [...value, ...duplicatedRows];
    setSelectedRowKeys([]);

    onChange({
      componentId: config.id,
      value: newData,
      pageId
    });
  }, [value, selectedRowKeys, onChange, config.id, pageId]);

  const handleAddColumn = useCallback((columnId: string) => {
    setVisibleColumnIds(prev => new Set([...prev, columnId]));
  }, []);

  const handleRemoveColumn = useCallback((columnId: string) => {
    setVisibleColumnIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(columnId);
      return newSet;
    });
  }, []);

  const renderCell = (column: GridColumn, record: GridRow, rowIndex: number) => {
    const cellValue = record[column.id];

    switch (column.type) {
      case 'text':
        return (
          <Input
            value={cellValue || ''}
            placeholder={column.placeholder}
            disabled={column.disabled || config.disabled}
            onChange={(e) => handleCellChange(rowIndex, column.id, e.target.value)}
            style={{ border: 'none', padding: '4px 8px' }}
          />
        );

      case 'dropdown':
        // Use dynamic options if available, fallback to static options
        const optionsToRender = (column as any).dynamicLoad 
          ? dynamicColumnOptions[column.id] || []
          : column.options || [];
        const isLoading = loadingColumns[column.id] || false;
        
        // Debug: Log dropdown cell rendering status
        if ((column as any).dynamicLoad) {
          if (optionsToRender.length === 0) {
            logger.debug('❌ Dropdown cell has no options:', {
              columnId: column.id,
              allDynamicOptionsKeys: Object.keys(dynamicColumnOptions),
              optionsForThisColumn: dynamicColumnOptions[column.id],
              isLoading
            });
          } else {
            logger.debug('✅ Dropdown cell has options:', {
              columnId: column.id,
              optionsCount: optionsToRender.length
            });
          }
        }
        
        return (
          <Select
            value={cellValue}
            placeholder={isLoading ? 'Loading options...' : column.placeholder}
            disabled={column.disabled || config.disabled || isLoading}
            onChange={(newValue) => handleCellChange(rowIndex, column.id, newValue)}
            style={{ width: '100%', border: 'none' }}
            size="small"
            allowClear={column.allowClear}
            showSearch={column.searchable}
            loading={isLoading}
          >
            {optionsToRender.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        );

      case 'checkbox':
        return (
          <Checkbox
            checked={cellValue || false}
            disabled={column.disabled || config.disabled}
            onChange={(e) => handleCellChange(rowIndex, column.id, e.target.checked)}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={cellValue || ''}
            placeholder={column.placeholder}
            disabled={column.disabled || config.disabled}
            onChange={(e) => handleCellChange(rowIndex, column.id, e.target.value)}
            style={{ border: 'none', padding: '4px 8px' }}
          />
        );

      default:
        return (
          <span>{cellValue || ''}</span>
        );
    }
  };

  // Prepare columns for the resizable header hook
  const baseColumns = useMemo(() => [
    ...(gridOptions.showRowNumbers !== false ? [{
      title: '#',
      key: 'rowNumber',
      width: 50,
      fixed: 'left' as const,
      render: (_: any, __: any, index: number) => index + 1
    }] : []),
    ...visibleColumns.map(column => {
      // Pin columns based on configuration (default to 2 if not specified)
      const lockedColumnsCount = gridOptions.lockedColumns ?? 2;
      const columnIndex = visibleColumns.indexOf(column);
      
      // Pin fieldname column (always) and columns within the locked range
      const shouldPin = column.id === 'fieldname' || (!column.optional && columnIndex < lockedColumnsCount);
      
      // Determine if this is the last locked column (for visual separator)
      const isLastLockedColumn = shouldPin && (
        // If fieldname is present and this is it, and it's the only locked column
        (column.id === 'fieldname' && lockedColumnsCount === 1) ||
        // If this is the last non-optional column within the locked range
        (!column.optional && columnIndex === lockedColumnsCount - 1) ||
        // Special case: if fieldname is present and this is the last locked non-fieldname column
        (column.id !== 'fieldname' && !column.optional && 
         visibleColumns.some(c => c.id === 'fieldname') && 
         columnIndex === lockedColumnsCount - 1)
      );
      
      return {
        title: column.optional ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{column.label}</span>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => handleRemoveColumn(column.id)}
              style={{ 
                marginLeft: 8, 
                minWidth: 'auto', 
                width: 16, 
                height: 16,
                fontSize: 10,
                color: '#999'
              }}
            />
          </div>
        ) : column.label,
        key: column.id,
        dataIndex: column.id,
        width: column.width || 120,
        fixed: shouldPin ? 'left' as const : undefined,
        render: (text: any, record: GridRow, index: number) => renderCell(column, record, index),
        // Add resizable configuration
        resizable: column.resizable !== false, // Default to true unless explicitly disabled
        minWidth: column.minWidth || 60,
        maxWidth: column.maxWidth,
        // Add visual separator for last locked column
        className: isLastLockedColumn ? 'locked-column-separator' : undefined,
        onHeaderCell: isLastLockedColumn ? () => ({
          style: {
            borderRight: '3px groove #d9d9d9',
            position: 'relative' as const
          }
        }) : undefined,
        onCell: isLastLockedColumn ? () => ({
          style: {
            borderRight: '3px groove #d9d9d9',
            position: 'relative' as const
          }
        }) : undefined
      };
    })
  ], [visibleColumns, gridOptions.showRowNumbers, handleRemoveColumn, dynamicColumnOptions]);

  // Use the resizable header hook
  const { 
    resizableColumns, 
    components: resizableComponents, 
    tableWidth,
    resetColumns 
  } = useAntdResizableHeader({
    columns: baseColumns,
    // Use config.id as persistence key to maintain column widths per component
    columnsState: {
      persistenceKey: `datagrid-${config.id}`,
      persistenceType: 'localStorage',
    },
  });

  const columns = useMemo(() => {
    return resizableColumns;
  }, [resizableColumns]);

  const rowSelection = gridOptions.allowRowSelection !== false ? {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    type: 'checkbox' as const,
    fixed: true,
    columnWidth: 50
  } : undefined;

  const validateRules = () => {
    const rules: any[] = [];
    
    if (config.required) {
      rules.push({
        required: true,
        message: `${config.label} is required`
      });
    }
    
    if (gridOptions.minRows) {
      rules.push({
        validator: (_: any, value: GridRow[]) => {
          if (value && value.length < gridOptions.minRows!) {
            return Promise.reject(new Error(`At least ${gridOptions.minRows} rows are required`));
          }
          return Promise.resolve();
        }
      });
    }
    
    return rules;
  };

  const formItemProps = {
    validateStatus: error && error.length > 0 ? 'error' as const : undefined,
    help: error && error.length > 0 ? error[0] : undefined,
    rules: validateRules()
  };

  return (
    <Form.Item {...formItemProps}>
      <div>
        {/* CSS for locked column separator */}
        <style dangerouslySetInnerHTML={{
          __html: `
            .ant-table .locked-column-separator .ant-table-cell {
              border-right: 3px groove #d9d9d9 !important;
              position: relative;
            }
            .ant-table .locked-column-separator th.ant-table-cell {
              border-right: 3px groove #d9d9d9 !important;
              position: relative;
            }
            .ant-table .locked-column-separator .ant-table-cell::after {
              content: '';
              position: absolute;
              top: 0;
              right: -2px;
              bottom: 0;
              width: 1px;
              background: linear-gradient(to bottom, 
                rgba(0, 0, 0, 0.1) 0%, 
                rgba(0, 0, 0, 0.05) 50%, 
                rgba(0, 0, 0, 0.1) 100%);
              pointer-events: none;
            }
          `
        }} />
        {/* Grid Title */}
        {config.label && (
          <div style={{ 
            marginBottom: '8px',
            fontWeight: 500,
            fontSize: '14px'
          }}>
            {config.label}
            {config.required && <span style={{ color: '#ff4d4f', marginLeft: '4px' }}>*</span>}
          </div>
        )}
        
        {/* Description */}
        {config.description && (
          <div style={{ 
            marginBottom: '12px',
            fontSize: '12px',
            color: '#666'
          }}>
            {config.description}
          </div>
        )}

        {/* Error Display */}
        {error && error.length > 0 && (
          <div style={{ 
            marginBottom: '12px',
            fontSize: '12px',
            color: '#ff4d4f'
          }}>
            {error[0]}
          </div>
        )}

        {/* Toolbar */}
        {gridOptions.showToolbar !== false && (
          <div style={{ 
            marginBottom: 8, 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Space>
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={handleAddRow}
                disabled={config.disabled}
              >
                Add Row
              </Button>
              {selectedRowKeys.length > 0 && (
                <>
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={handleDuplicateRows}
                    disabled={config.disabled}
                  >
                    Duplicate
                  </Button>
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleDeleteRows}
                    disabled={config.disabled}
                  >
                    Delete
                  </Button>
                </>
              )}
              {availableOptionalColumns.length > 0 && (
                <Dropdown
                  menu={{
                    items: availableOptionalColumns.map(column => ({
                      key: column.id,
                      label: column.label,
                      onClick: () => handleAddColumn(column.id)
                    }))
                  }}
                  disabled={config.disabled}
                >
                  <Button
                    size="small"
                    icon={<SettingOutlined />}
                  >
                    Add Column
                  </Button>
                </Dropdown>
              )}
              <Tooltip title="Reset column widths to default">
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => resetColumns()}
                  disabled={config.disabled}
                >
                  Reset Widths
                </Button>
              </Tooltip>
            </Space>
            <span style={{ fontSize: '12px', color: '#666' }}>
              {value.length} row{value.length !== 1 ? 's' : ''}
              {selectedRowKeys.length > 0 && ` (${selectedRowKeys.length} selected)`}
            </span>
          </div>
        )}

        {/* Grid */}
        <Table
          columns={columns}
          components={resizableComponents}
          dataSource={value.map((row, index) => ({ ...row, key: index }))}
          rowSelection={rowSelection}
          pagination={false}
          size="small"
          bordered
          scroll={{ 
            x: tableWidth,
            y: gridOptions.maxHeight ? parseInt(gridOptions.maxHeight.replace('px', '')) - 100 : 300
          }}
          style={{
            maxHeight: gridOptions.maxHeight || '400px'
          }}
          locale={{
            emptyText: gridOptions.emptyText || 'No data'
          }}
        />

        {/* Footer info */}
        {gridOptions.showFooter !== false && (
          <div style={{ 
            marginTop: 8, 
            fontSize: '12px', 
            color: '#666',
            textAlign: 'right'
          }}>
            {gridOptions.minRows && `Min: ${gridOptions.minRows} rows`}
            {gridOptions.maxRows && ` • Max: ${gridOptions.maxRows} rows`}
          </div>
        )}
      </div>
    </Form.Item>
  );
}; 